import { useState, useEffect, useCallback } from 'react';
import { 
  Gamepad2, RotateCcw, Zap, Target, Brain, Cpu, 
  Timer, Trophy, ChevronRight, Grid3X3, Type, HelpCircle,
  Crosshair, Sparkles, Users, Award
} from 'lucide-react';

// ============================================
// GAME 1: Tic-Tac-Toe (Enhanced)
// ============================================
const TicTacToe = () => {
  const [board, setBoard] = useState(Array(9).fill(null));
  const [isXNext, setIsXNext] = useState(true);
  const [winner, setWinner] = useState(null);
  const [winLine, setWinLine] = useState(null);

  const checkWinner = (squares) => {
    const lines = [
      [0, 1, 2], [3, 4, 5], [6, 7, 8],
      [0, 3, 6], [1, 4, 7], [2, 5, 8],
      [0, 4, 8], [2, 4, 6]
    ];
    for (let line of lines) {
      const [a, b, c] = line;
      if (squares[a] && squares[a] === squares[b] && squares[a] === squares[c]) {
        return { winner: squares[a], line };
      }
    }
    return squares.every(s => s) ? { winner: 'draw', line: null } : null;
  };

  const makeAIMove = (squares) => {
    const empty = squares.map((s, i) => s === null ? i : null).filter(i => i !== null);
    if (empty.length === 0) return squares;
    
    // Simple AI: try to win, block, or random
    const lines = [[0,1,2],[3,4,5],[6,7,8],[0,3,6],[1,4,7],[2,5,8],[0,4,8],[2,4,6]];
    
    // Try to win
    for (let [a,b,c] of lines) {
      if (squares[a] === 'O' && squares[b] === 'O' && squares[c] === null) return c;
      if (squares[a] === 'O' && squares[c] === 'O' && squares[b] === null) return b;
      if (squares[b] === 'O' && squares[c] === 'O' && squares[a] === null) return a;
    }
    
    // Block player
    for (let [a,b,c] of lines) {
      if (squares[a] === 'X' && squares[b] === 'X' && squares[c] === null) return c;
      if (squares[a] === 'X' && squares[c] === 'X' && squares[b] === null) return b;
      if (squares[b] === 'X' && squares[c] === 'X' && squares[a] === null) return a;
    }
    
    // Take center or random
    if (squares[4] === null) return 4;
    return empty[Math.floor(Math.random() * empty.length)];
  };

  const handleClick = (i) => {
    if (board[i] || winner) return;
    
    let newBoard = [...board];
    newBoard[i] = 'X';
    
    let result = checkWinner(newBoard);
    if (result) {
      setBoard(newBoard);
      setWinner(result.winner);
      setWinLine(result.line);
      return;
    }

    const aiMove = makeAIMove(newBoard);
    newBoard[aiMove] = 'O';
    result = checkWinner(newBoard);
    setBoard(newBoard);
    if (result) {
      setWinner(result.winner);
      setWinLine(result.line);
    }
  };

  const reset = () => {
    setBoard(Array(9).fill(null));
    setIsXNext(true);
    setWinner(null);
    setWinLine(null);
  };

  return (
    <div className="flex flex-col items-center" data-testid="tictactoe-game">
      <div className="grid grid-cols-3 gap-2 mb-6">
        {board.map((cell, i) => (
          <button
            key={i}
            onClick={() => handleClick(i)}
            className={`w-20 h-20 bg-[#0d0d12] border-2 rounded-lg flex items-center justify-center text-3xl font-mono font-bold transition-all duration-300 ${
              winLine?.includes(i) 
                ? 'border-[#00ff88] bg-[#00ff88]/10 scale-105' 
                : 'border-[#1a1a24] hover:border-[#00d4ff]/50 hover:bg-[#00d4ff]/5'
            }`}
            data-testid={`cell-${i}`}
          >
            <span className={`${
              cell === 'X' 
                ? 'text-[#00d4ff] drop-shadow-[0_0_8px_rgba(0,212,255,0.6)]' 
                : 'text-[#ff3366] drop-shadow-[0_0_8px_rgba(255,51,102,0.6)]'
            }`}>
              {cell}
            </span>
          </button>
        ))}
      </div>
      
      {winner && (
        <div className={`text-center mb-4 px-6 py-3 rounded-lg border ${
          winner === 'X' 
            ? 'border-[#00d4ff]/50 bg-[#00d4ff]/10 text-[#00d4ff]'
            : winner === 'O'
              ? 'border-[#ff3366]/50 bg-[#ff3366]/10 text-[#ff3366]'
              : 'border-[#ffd700]/50 bg-[#ffd700]/10 text-[#ffd700]'
        }`}>
          <p className="font-mono text-sm uppercase tracking-wider">
            {winner === 'draw' ? "Draw Game" : `${winner === 'X' ? 'You Win!' : 'AI Wins'}`}
          </p>
        </div>
      )}
      
      <button
        onClick={reset}
        className="flex items-center gap-2 px-4 py-2 border border-[#00d4ff]/30 text-[#00d4ff] font-mono text-xs uppercase tracking-wider hover:bg-[#00d4ff]/10 transition-colors rounded"
        data-testid="reset-ttt"
      >
        <RotateCcw className="w-3.5 h-3.5" /> New Game
      </button>
    </div>
  );
};

// ============================================
// GAME 2: Memory Match
// ============================================
const MemoryMatch = () => {
  const symbols = ['⚡', '🔥', '💎', '🚀', '⭐', '🎯', '💫', '🌙'];
  const [cards, setCards] = useState([]);
  const [flipped, setFlipped] = useState([]);
  const [matched, setMatched] = useState([]);
  const [moves, setMoves] = useState(0);
  const [gameComplete, setGameComplete] = useState(false);

  const initGame = useCallback(() => {
    const shuffled = [...symbols, ...symbols]
      .sort(() => Math.random() - 0.5)
      .map((symbol, i) => ({ id: i, symbol, flipped: false }));
    setCards(shuffled);
    setFlipped([]);
    setMatched([]);
    setMoves(0);
    setGameComplete(false);
  }, []);

  useEffect(() => {
    initGame();
  }, [initGame]);

  const handleCardClick = (id) => {
    if (flipped.length === 2 || flipped.includes(id) || matched.includes(id)) return;
    
    const newFlipped = [...flipped, id];
    setFlipped(newFlipped);
    
    if (newFlipped.length === 2) {
      setMoves(m => m + 1);
      const [first, second] = newFlipped;
      if (cards[first].symbol === cards[second].symbol) {
        const newMatched = [...matched, first, second];
        setMatched(newMatched);
        setFlipped([]);
        if (newMatched.length === cards.length) {
          setGameComplete(true);
        }
      } else {
        setTimeout(() => setFlipped([]), 800);
      }
    }
  };

  return (
    <div className="flex flex-col items-center" data-testid="memory-game">
      <div className="flex justify-between w-full max-w-xs mb-4">
        <span className="font-mono text-xs text-[#4a4a60]">Moves: <span className="text-[#00d4ff]">{moves}</span></span>
        <span className="font-mono text-xs text-[#4a4a60]">Matched: <span className="text-[#00ff88]">{matched.length/2}/{symbols.length}</span></span>
      </div>
      
      <div className="grid grid-cols-4 gap-2 mb-6">
        {cards.map((card) => (
          <button
            key={card.id}
            onClick={() => handleCardClick(card.id)}
            className={`w-16 h-16 rounded-lg flex items-center justify-center text-2xl transition-all duration-300 transform ${
              flipped.includes(card.id) || matched.includes(card.id)
                ? 'bg-[#00d4ff]/10 border-2 border-[#00d4ff]/50 rotate-0'
                : 'bg-[#0d0d12] border-2 border-[#1a1a24] hover:border-[#00d4ff]/30 rotate-y-180'
            } ${matched.includes(card.id) ? 'border-[#00ff88]/50 bg-[#00ff88]/10' : ''}`}
          >
            {(flipped.includes(card.id) || matched.includes(card.id)) ? card.symbol : '?'}
          </button>
        ))}
      </div>
      
      {gameComplete && (
        <div className="text-center mb-4 px-6 py-3 rounded-lg border border-[#00ff88]/50 bg-[#00ff88]/10">
          <p className="text-[#00ff88] font-mono text-sm uppercase">Complete in {moves} moves!</p>
        </div>
      )}
      
      <button
        onClick={initGame}
        className="flex items-center gap-2 px-4 py-2 border border-[#00d4ff]/30 text-[#00d4ff] font-mono text-xs uppercase tracking-wider hover:bg-[#00d4ff]/10 transition-colors rounded"
      >
        <RotateCcw className="w-3.5 h-3.5" /> Reset
      </button>
    </div>
  );
};

// ============================================
// GAME 3: Reaction Time
// ============================================
const ReactionTime = () => {
  const [state, setState] = useState('waiting'); // waiting, ready, go, result
  const [startTime, setStartTime] = useState(0);
  const [reactionTime, setReactionTime] = useState(null);
  const [bestTime, setBestTime] = useState(() => {
    return parseInt(localStorage.getItem('sage_reaction_best') || '9999');
  });

  const startGame = () => {
    setState('ready');
    const delay = 2000 + Math.random() * 3000;
    setTimeout(() => {
      setState('go');
      setStartTime(Date.now());
    }, delay);
  };

  const handleClick = () => {
    if (state === 'waiting') {
      startGame();
    } else if (state === 'ready') {
      setState('waiting');
      setReactionTime('Too early!');
    } else if (state === 'go') {
      const time = Date.now() - startTime;
      setReactionTime(time);
      setState('result');
      if (time < bestTime) {
        setBestTime(time);
        localStorage.setItem('sage_reaction_best', time.toString());
      }
    } else if (state === 'result') {
      setState('waiting');
      setReactionTime(null);
    }
  };

  return (
    <div className="flex flex-col items-center" data-testid="reaction-game">
      <div className="text-center mb-6">
        <p className="font-mono text-xs text-[#4a4a60] uppercase tracking-wider mb-2">Best Time</p>
        <p className="font-mono text-2xl text-[#ffd700]">
          {bestTime === 9999 ? '---' : `${bestTime}ms`}
        </p>
      </div>
      
      <button
        onClick={handleClick}
        className={`w-64 h-64 rounded-xl flex flex-col items-center justify-center transition-all duration-300 ${
          state === 'waiting' 
            ? 'bg-[#0d0d12] border-2 border-[#1a1a24] hover:border-[#00d4ff]/50'
            : state === 'ready'
              ? 'bg-[#ff3366]/20 border-2 border-[#ff3366]/50'
              : state === 'go'
                ? 'bg-[#00ff88]/20 border-2 border-[#00ff88]/50 animate-pulse'
                : 'bg-[#00d4ff]/10 border-2 border-[#00d4ff]/50'
        }`}
      >
        {state === 'waiting' && (
          <>
            <Zap className="w-12 h-12 text-[#00d4ff] mb-4" />
            <p className="font-mono text-sm text-[#00d4ff] uppercase">Click to Start</p>
          </>
        )}
        {state === 'ready' && (
          <>
            <Timer className="w-12 h-12 text-[#ff3366] mb-4 animate-pulse" />
            <p className="font-mono text-sm text-[#ff3366] uppercase">Wait for Green...</p>
          </>
        )}
        {state === 'go' && (
          <>
            <Target className="w-12 h-12 text-[#00ff88] mb-4" />
            <p className="font-mono text-lg text-[#00ff88] uppercase font-bold">CLICK NOW!</p>
          </>
        )}
        {state === 'result' && (
          <>
            <Trophy className="w-12 h-12 text-[#ffd700] mb-4" />
            <p className="font-mono text-3xl text-[#00d4ff] font-bold">
              {typeof reactionTime === 'number' ? `${reactionTime}ms` : reactionTime}
            </p>
            <p className="font-mono text-xs text-[#4a4a60] mt-2 uppercase">Click to try again</p>
          </>
        )}
      </button>
    </div>
  );
};

// ============================================
// GAME 4: Word Scramble (Enhanced)
// ============================================
const WordScramble = () => {
  const words = ['SYSTEM', 'PYTHON', 'REACT', 'DATABASE', 'NEURAL', 'QUANTUM', 'CIPHER', 'MATRIX', 'BINARY', 'VECTOR'];
  const [currentWord, setCurrentWord] = useState(() => words[Math.floor(Math.random() * words.length)]);
  const [scrambled, setScrambled] = useState(() => shuffleWord(currentWord));
  const [guess, setGuess] = useState('');
  const [message, setMessage] = useState('');
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);

  function shuffleWord(word) {
    let shuffled = word.split('').sort(() => Math.random() - 0.5).join('');
    while (shuffled === word) {
      shuffled = word.split('').sort(() => Math.random() - 0.5).join('');
    }
    return shuffled;
  }

  const handleGuess = () => {
    if (guess.toUpperCase() === currentWord) {
      setMessage('CORRECT!');
      setScore(s => s + (10 * (streak + 1)));
      setStreak(s => s + 1);
      setTimeout(newWord, 800);
    } else {
      setMessage('Try again');
      setStreak(0);
    }
  };

  const newWord = () => {
    const word = words[Math.floor(Math.random() * words.length)];
    setCurrentWord(word);
    setScrambled(shuffleWord(word));
    setGuess('');
    setMessage('');
  };

  return (
    <div className="text-center max-w-sm mx-auto" data-testid="wordscramble-game">
      <div className="flex justify-between mb-6">
        <div>
          <p className="font-mono text-[9px] text-[#4a4a60] uppercase">Score</p>
          <p className="font-mono text-xl text-[#00d4ff]">{score}</p>
        </div>
        <div>
          <p className="font-mono text-[9px] text-[#4a4a60] uppercase">Streak</p>
          <p className="font-mono text-xl text-[#ffd700]">{streak}x</p>
        </div>
      </div>
      
      <p className="text-[#4a4a60] font-mono text-[10px] uppercase tracking-wider mb-3">Unscramble</p>
      
      <div className="flex justify-center gap-1 mb-6">
        {scrambled.split('').map((letter, i) => (
          <div 
            key={i}
            className="w-10 h-12 bg-[#0d0d12] border border-[#00d4ff]/30 rounded flex items-center justify-center"
          >
            <span className="text-xl font-mono font-bold text-[#00d4ff]">{letter}</span>
          </div>
        ))}
      </div>
      
      <input
        type="text"
        value={guess}
        onChange={(e) => setGuess(e.target.value.toUpperCase())}
        onKeyDown={(e) => e.key === 'Enter' && handleGuess()}
        className="w-full bg-[#0d0d12] border border-[#1a1a24] text-[#e8e8f0] px-4 py-3 text-center font-mono uppercase tracking-widest mb-4 focus:border-[#00d4ff]/50 outline-none"
        placeholder="TYPE YOUR ANSWER"
        data-testid="scramble-input"
      />
      
      <div className="flex gap-2 justify-center mb-4">
        <button
          onClick={handleGuess}
          className="flex-1 border border-[#00d4ff] text-[#00d4ff] font-mono text-xs uppercase py-3 hover:bg-[#00d4ff]/10 transition-colors"
          data-testid="check-guess-btn"
        >
          Submit
        </button>
        <button
          onClick={() => { newWord(); setStreak(0); }}
          className="border border-[#1a1a24] text-[#4a4a60] font-mono text-xs uppercase px-4 py-3 hover:border-[#00d4ff]/30 transition-colors"
          data-testid="skip-word-btn"
        >
          Skip
        </button>
      </div>
      
      {message && (
        <p className={`font-mono text-sm uppercase tracking-wider ${
          message === 'CORRECT!' ? 'text-[#00ff88]' : 'text-[#ff3366]'
        }`}>
          {message}
        </p>
      )}
    </div>
  );
};

// ============================================
// GAME 5: AI Trivia Battle (Enhanced with AI & Multiplayer)
// ============================================
const Trivia = () => {
  // Large question bank - randomly selected each game
  const questionBank = [
    // Tech & Programming
    { q: 'What does CPU stand for?', a: ['Central Processing Unit', 'Computer Personal Unit', 'Central Personal Unit', 'Control Processing Unit'], c: 0, cat: 'Tech' },
    { q: 'What language is React written in?', a: ['Python', 'JavaScript', 'Java', 'C++'], c: 1, cat: 'Tech' },
    { q: 'What does API stand for?', a: ['Application Programming Interface', 'Advanced Programming Input', 'Application Protocol Interface', 'Advanced Protocol Input'], c: 0, cat: 'Tech' },
    { q: 'What is Git used for?', a: ['Database management', 'Version control', 'Web hosting', 'Graphics design'], c: 1, cat: 'Tech' },
    { q: 'What does CSS stand for?', a: ['Creative Style Sheets', 'Cascading Style Sheets', 'Computer Style Sheets', 'Colorful Style Sheets'], c: 1, cat: 'Tech' },
    { q: 'What is MongoDB?', a: ['SQL Database', 'NoSQL Database', 'Programming Language', 'Web Framework'], c: 1, cat: 'Tech' },
    { q: 'What port does HTTP use?', a: ['21', '22', '80', '443'], c: 2, cat: 'Tech' },
    { q: 'What does RAM stand for?', a: ['Read Access Memory', 'Random Access Memory', 'Run Application Memory', 'Rapid Action Memory'], c: 1, cat: 'Tech' },
    { q: 'What is Docker used for?', a: ['Containerization', 'Database', 'Frontend framework', 'Version control'], c: 0, cat: 'Tech' },
    { q: 'What does JSON stand for?', a: ['JavaScript Object Notation', 'Java Standard Object Notation', 'JavaScript Online Notation', 'Java Syntax Object Network'], c: 0, cat: 'Tech' },
    { q: 'Which company developed TypeScript?', a: ['Google', 'Microsoft', 'Facebook', 'Apple'], c: 1, cat: 'Tech' },
    { q: 'What is the time complexity of binary search?', a: ['O(n)', 'O(log n)', 'O(n²)', 'O(1)'], c: 1, cat: 'Tech' },
    { q: 'What does SQL stand for?', a: ['Structured Query Language', 'Simple Query Language', 'Standard Query Logic', 'System Query Language'], c: 0, cat: 'Tech' },
    { q: 'Which protocol is used for secure web browsing?', a: ['HTTP', 'FTP', 'HTTPS', 'SMTP'], c: 2, cat: 'Tech' },
    { q: 'What is the main purpose of npm?', a: ['Code compilation', 'Package management', 'Version control', 'Testing'], c: 1, cat: 'Tech' },
    // Science
    { q: 'What is the chemical symbol for gold?', a: ['Go', 'Gd', 'Au', 'Ag'], c: 2, cat: 'Science' },
    { q: 'How many planets are in our solar system?', a: ['7', '8', '9', '10'], c: 1, cat: 'Science' },
    { q: 'What is the speed of light?', a: ['299,792 km/s', '199,792 km/s', '399,792 km/s', '150,000 km/s'], c: 0, cat: 'Science' },
    { q: 'What is the powerhouse of the cell?', a: ['Nucleus', 'Ribosome', 'Mitochondria', 'Endoplasmic reticulum'], c: 2, cat: 'Science' },
    { q: 'What gas do plants absorb?', a: ['Oxygen', 'Nitrogen', 'Carbon Dioxide', 'Hydrogen'], c: 2, cat: 'Science' },
    { q: 'What is the largest organ in the human body?', a: ['Heart', 'Liver', 'Brain', 'Skin'], c: 3, cat: 'Science' },
    { q: 'What is the atomic number of Carbon?', a: ['4', '6', '8', '12'], c: 1, cat: 'Science' },
    { q: 'What planet is known as the Red Planet?', a: ['Venus', 'Mars', 'Jupiter', 'Saturn'], c: 1, cat: 'Science' },
    // General Knowledge
    { q: 'What year did World War II end?', a: ['1943', '1944', '1945', '1946'], c: 2, cat: 'History' },
    { q: 'What is the capital of Japan?', a: ['Seoul', 'Beijing', 'Tokyo', 'Bangkok'], c: 2, cat: 'Geography' },
    { q: 'Who painted the Mona Lisa?', a: ['Michelangelo', 'Leonardo da Vinci', 'Raphael', 'Donatello'], c: 1, cat: 'Art' },
    { q: 'What is the largest ocean?', a: ['Atlantic', 'Indian', 'Arctic', 'Pacific'], c: 3, cat: 'Geography' },
    { q: 'How many continents are there?', a: ['5', '6', '7', '8'], c: 2, cat: 'Geography' },
    { q: 'What is the currency of the United Kingdom?', a: ['Euro', 'Dollar', 'Pound Sterling', 'Franc'], c: 2, cat: 'Geography' },
    { q: 'Who wrote "1984"?', a: ['Aldous Huxley', 'George Orwell', 'Ray Bradbury', 'H.G. Wells'], c: 1, cat: 'Literature' },
    { q: 'What is the hardest natural substance?', a: ['Gold', 'Iron', 'Diamond', 'Platinum'], c: 2, cat: 'Science' },
    // Crypto & Finance
    { q: 'What year was Bitcoin created?', a: ['2007', '2008', '2009', '2010'], c: 2, cat: 'Crypto' },
    { q: 'Who created Bitcoin?', a: ['Vitalik Buterin', 'Satoshi Nakamoto', 'Charlie Lee', 'Elon Musk'], c: 1, cat: 'Crypto' },
    { q: 'What blockchain does ETH run on?', a: ['Bitcoin', 'Solana', 'Ethereum', 'Cardano'], c: 2, cat: 'Crypto' },
    { q: 'What does NFT stand for?', a: ['New File Type', 'Non-Fungible Token', 'Network File Transfer', 'National Finance Term'], c: 1, cat: 'Crypto' },
    { q: 'What is a "smart contract"?', a: ['Legal document', 'Self-executing code', 'AI assistant', 'Trading bot'], c: 1, cat: 'Crypto' },
    // AI & Future Tech
    { q: 'What does AI stand for?', a: ['Automated Intelligence', 'Artificial Intelligence', 'Advanced Integration', 'Autonomous Interface'], c: 1, cat: 'AI' },
    { q: 'What company created ChatGPT?', a: ['Google', 'Microsoft', 'OpenAI', 'Meta'], c: 2, cat: 'AI' },
    { q: 'What does LLM stand for in AI?', a: ['Large Language Model', 'Linear Logic Machine', 'Learning Logic Model', 'Linguistic Learning Module'], c: 0, cat: 'AI' },
    { q: 'What is "machine learning"?', a: ['Robots building robots', 'AI learning from data', 'Computer repairs', 'Hardware upgrades'], c: 1, cat: 'AI' },
    { q: 'What does GPU stand for?', a: ['General Processing Unit', 'Graphics Processing Unit', 'Global Power Unit', 'Gaming Performance Unit'], c: 1, cat: 'AI' },
  ];

  const [gameMode, setGameMode] = useState(null); // null, 'solo', 'vsAI', 'waiting'
  const [questions, setQuestions] = useState([]);
  const [currentQ, setCurrentQ] = useState(0);
  const [playerScore, setPlayerScore] = useState(0);
  const [aiScore, setAiScore] = useState(0);
  const [showResult, setShowResult] = useState(false);
  const [selected, setSelected] = useState(null);
  const [aiSelected, setAiSelected] = useState(null);
  const [timeLeft, setTimeLeft] = useState(12);
  const [category, setCategory] = useState('All');
  
  const categories = ['All', 'Tech', 'Science', 'AI', 'Crypto', 'Geography', 'History'];

  // Generate random questions for each game
  const startGame = (mode) => {
    let pool = category === 'All' 
      ? questionBank 
      : questionBank.filter(q => q.cat === category);
    
    // Shuffle and pick 10 questions
    const shuffled = [...pool].sort(() => Math.random() - 0.5);
    setQuestions(shuffled.slice(0, 10));
    setGameMode(mode);
    setCurrentQ(0);
    setPlayerScore(0);
    setAiScore(0);
    setShowResult(false);
    setSelected(null);
    setAiSelected(null);
    setTimeLeft(12);
  };

  // AI opponent logic
  const makeAIMove = useCallback(() => {
    if (gameMode !== 'vsAI' || !questions[currentQ]) return;
    
    // AI has 70% chance to get it right, answers between 2-8 seconds
    const aiDelay = 2000 + Math.random() * 6000;
    const isCorrect = Math.random() < 0.7;
    const correctAnswer = questions[currentQ].c;
    
    setTimeout(() => {
      if (selected !== null) return; // Player already answered
      
      if (isCorrect) {
        setAiSelected(correctAnswer);
        setAiScore(s => s + Math.ceil((12 - (aiDelay/1000)) * 10));
      } else {
        // Pick wrong answer
        const wrongAnswers = [0, 1, 2, 3].filter(i => i !== correctAnswer);
        setAiSelected(wrongAnswers[Math.floor(Math.random() * wrongAnswers.length)]);
      }
    }, aiDelay);
  }, [gameMode, questions, currentQ, selected]);

  useEffect(() => {
    if (gameMode === 'vsAI' && selected === null && !showResult) {
      makeAIMove();
    }
  }, [currentQ, gameMode, makeAIMove, selected, showResult]);

  useEffect(() => {
    if (!gameMode || showResult || selected !== null) return;
    
    const timer = setInterval(() => {
      setTimeLeft(t => {
        if (t <= 1) {
          handleTimeout();
          return 12;
        }
        return t - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [currentQ, showResult, selected, gameMode]);

  const handleTimeout = () => {
    nextQuestion();
  };

  const nextQuestion = () => {
    if (currentQ < questions.length - 1) {
      setCurrentQ(c => c + 1);
      setSelected(null);
      setAiSelected(null);
      setTimeLeft(12);
    } else {
      setShowResult(true);
    }
  };

  const handleAnswer = (idx) => {
    if (selected !== null) return;
    setSelected(idx);
    
    if (idx === questions[currentQ].c) {
      setPlayerScore(s => s + Math.ceil(timeLeft * 10));
    }
    
    setTimeout(() => {
      nextQuestion();
    }, 1500);
  };

  // Game Mode Selection
  if (!gameMode) {
    return (
      <div className="text-center" data-testid="trivia-menu">
        <div className="w-16 h-16 mx-auto mb-4 border border-[#00d4ff]/30 rounded-xl flex items-center justify-center bg-[#00d4ff]/5">
          <HelpCircle className="w-8 h-8 text-[#00d4ff]" style={{ filter: 'drop-shadow(0 0 4px rgba(0, 212, 255, 0.6))' }} />
        </div>
        <h2 className="font-mono text-lg text-[#00d4ff] mb-2">Trivia Battle</h2>
        <p className="font-mono text-xs text-[#4a4a60] mb-6">Test your knowledge</p>
        
        {/* Category Selection */}
        <div className="mb-6">
          <p className="font-mono text-[9px] text-[#4a4a60] uppercase tracking-wider mb-2">Category</p>
          <div className="flex flex-wrap justify-center gap-2">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setCategory(cat)}
                className={`px-3 py-1 rounded font-mono text-[10px] uppercase transition-all ${
                  category === cat 
                    ? 'bg-[#00d4ff]/20 border border-[#00d4ff]/50 text-[#00d4ff]'
                    : 'bg-[#0d0d12] border border-[#1a1a24] text-[#4a4a60] hover:border-[#00d4ff]/30'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
        
        {/* Game Mode Selection */}
        <div className="space-y-3">
          <button
            onClick={() => startGame('solo')}
            className="w-full p-4 bg-[#0d0d12] border border-[#1a1a24] rounded-lg hover:border-[#00d4ff]/50 transition-all group"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-[#00d4ff]/10 border border-[#00d4ff]/30 flex items-center justify-center group-hover:scale-110 transition-transform">
                <Brain className="w-6 h-6 text-[#00d4ff]" />
              </div>
              <div className="text-left">
                <p className="font-mono text-sm text-[#00d4ff]">Solo Mode</p>
                <p className="font-mono text-[10px] text-[#4a4a60]">Play alone, beat your score</p>
              </div>
            </div>
          </button>
          
          <button
            onClick={() => startGame('vsAI')}
            className="w-full p-4 bg-[#0d0d12] border border-[#1a1a24] rounded-lg hover:border-[#ff3366]/50 transition-all group"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-[#ff3366]/10 border border-[#ff3366]/30 flex items-center justify-center group-hover:scale-110 transition-transform">
                <Cpu className="w-6 h-6 text-[#ff3366]" />
              </div>
              <div className="text-left">
                <p className="font-mono text-sm text-[#ff3366]">VS Sage AI</p>
                <p className="font-mono text-[10px] text-[#4a4a60]">Challenge the AI opponent</p>
              </div>
            </div>
          </button>
          
          <button
            disabled
            className="w-full p-4 bg-[#0d0d12] border border-[#1a1a24] rounded-lg opacity-50 cursor-not-allowed"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-[#ffd700]/10 border border-[#ffd700]/30 flex items-center justify-center">
                <Users className="w-6 h-6 text-[#ffd700]" />
              </div>
              <div className="text-left">
                <p className="font-mono text-sm text-[#ffd700]">VS Friend</p>
                <p className="font-mono text-[10px] text-[#4a4a60]">Coming soon - Challenge friends</p>
              </div>
            </div>
          </button>
        </div>
      </div>
    );
  }

  // Results Screen
  if (showResult) {
    const playerWon = playerScore > aiScore;
    const tie = playerScore === aiScore;
    
    return (
      <div className="text-center" data-testid="trivia-result">
        <Trophy className={`w-16 h-16 mx-auto mb-4 ${playerWon ? 'text-[#ffd700]' : tie ? 'text-[#4a4a60]' : 'text-[#ff3366]'}`} />
        
        {gameMode === 'vsAI' ? (
          <>
            <p className="font-mono text-xs text-[#4a4a60] uppercase tracking-wider mb-2">
              {playerWon ? 'Victory!' : tie ? 'Draw!' : 'Defeated'}
            </p>
            <div className="flex justify-center gap-8 mb-6">
              <div>
                <p className="font-mono text-[10px] text-[#4a4a60]">You</p>
                <p className={`text-3xl font-mono font-bold ${playerWon ? 'text-[#00d4ff]' : 'text-[#4a4a60]'}`}>{playerScore}</p>
              </div>
              <div className="text-2xl text-[#4a4a60]">vs</div>
              <div>
                <p className="font-mono text-[10px] text-[#4a4a60]">Sage AI</p>
                <p className={`text-3xl font-mono font-bold ${!playerWon && !tie ? 'text-[#ff3366]' : 'text-[#4a4a60]'}`}>{aiScore}</p>
              </div>
            </div>
          </>
        ) : (
          <>
            <p className="font-mono text-xs text-[#4a4a60] uppercase tracking-wider mb-2">Final Score</p>
            <p className="text-5xl font-mono font-bold text-[#00d4ff] mb-6">{playerScore}</p>
          </>
        )}
        
        <div className="flex gap-2 justify-center">
          <button
            onClick={() => startGame(gameMode)}
            className="flex items-center gap-2 px-4 py-2 border border-[#00d4ff]/30 text-[#00d4ff] font-mono text-xs uppercase tracking-wider hover:bg-[#00d4ff]/10 transition-colors rounded"
          >
            <RotateCcw className="w-3.5 h-3.5" /> Rematch
          </button>
          <button
            onClick={() => setGameMode(null)}
            className="flex items-center gap-2 px-4 py-2 border border-[#1a1a24] text-[#4a4a60] font-mono text-xs uppercase tracking-wider hover:border-[#00d4ff]/30 transition-colors rounded"
          >
            Menu
          </button>
        </div>
      </div>
    );
  }

  // Game Screen
  return (
    <div data-testid="trivia-game">
      {/* Score Header for VS mode */}
      {gameMode === 'vsAI' && (
        <div className="flex justify-between items-center mb-4 p-3 bg-[#0d0d12] rounded-lg border border-[#1a1a24]">
          <div className="text-center">
            <p className="font-mono text-[9px] text-[#4a4a60]">You</p>
            <p className="font-mono text-lg text-[#00d4ff]">{playerScore}</p>
          </div>
          <div className="text-center">
            <p className="font-mono text-[10px] text-[#4a4a60]">{currentQ + 1}/{questions.length}</p>
          </div>
          <div className="text-center">
            <p className="font-mono text-[9px] text-[#4a4a60]">Sage AI</p>
            <p className="font-mono text-lg text-[#ff3366]">{aiScore}</p>
          </div>
        </div>
      )}
      
      {/* Solo mode header */}
      {gameMode === 'solo' && (
        <div className="flex justify-between items-center mb-4">
          <p className="font-mono text-xs text-[#4a4a60]">
            <span className="text-[#00d4ff]">{currentQ + 1}</span>/{questions.length}
          </p>
          <div className={`font-mono text-sm px-3 py-1 rounded ${
            timeLeft <= 5 ? 'text-[#ff3366] bg-[#ff3366]/10' : 'text-[#00d4ff] bg-[#00d4ff]/10'
          }`}>
            {timeLeft}s
          </div>
          <p className="font-mono text-xs text-[#ffd700]">{playerScore} pts</p>
        </div>
      )}
      
      {/* Timer bar */}
      <div className="h-1 bg-[#1a1a24] rounded-full mb-4 overflow-hidden">
        <div 
          className={`h-full transition-all duration-1000 ${
            timeLeft <= 5 
              ? 'bg-gradient-to-r from-[#ff3366] to-[#ff6644]' 
              : 'bg-gradient-to-r from-[#00d4ff] to-[#00ff88]'
          }`}
          style={{ width: `${(timeLeft / 12) * 100}%` }}
        />
      </div>
      
      {/* Category badge */}
      <div className="flex justify-center mb-3">
        <span className="px-2 py-1 bg-[#00d4ff]/10 border border-[#00d4ff]/30 rounded font-mono text-[9px] text-[#00d4ff] uppercase">
          {questions[currentQ]?.cat}
        </span>
      </div>
      
      {/* Question */}
      <p className="text-lg text-[#e8e8f0] mb-6 font-medium text-center">{questions[currentQ]?.q}</p>
      
      {/* Answers */}
      <div className="space-y-2">
        {questions[currentQ]?.a.map((answer, idx) => {
          const isCorrect = idx === questions[currentQ].c;
          const playerChose = selected === idx;
          const aiChose = aiSelected === idx;
          
          let borderColor = 'border-[#1a1a24]';
          let bgColor = 'bg-[#0d0d12]';
          let textColor = 'text-[#e8e8f0]';
          
          if (selected !== null || aiSelected !== null) {
            if (isCorrect) {
              borderColor = 'border-[#00ff88]/50';
              bgColor = 'bg-[#00ff88]/10';
              textColor = 'text-[#00ff88]';
            } else if (playerChose) {
              borderColor = 'border-[#ff3366]/50';
              bgColor = 'bg-[#ff3366]/10';
              textColor = 'text-[#ff3366]';
            } else if (aiChose && gameMode === 'vsAI') {
              borderColor = 'border-[#ff3366]/30';
              bgColor = 'bg-[#ff3366]/5';
            }
          }
          
          return (
            <button
              key={idx}
              onClick={() => handleAnswer(idx)}
              disabled={selected !== null}
              className={`w-full p-4 rounded-lg font-mono text-sm text-left transition-all ${bgColor} border-2 ${borderColor} ${textColor} ${
                selected === null ? 'hover:border-[#00d4ff]/50 hover:bg-[#00d4ff]/5' : ''
              }`}
              data-testid={`answer-${idx}`}
            >
              <div className="flex justify-between items-center">
                <span>{answer}</span>
                {gameMode === 'vsAI' && aiChose && (
                  <span className="text-[9px] text-[#ff3366] uppercase">AI</span>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ============================================
// GAME 6: Simon Says (Pattern Memory)
// ============================================
const SimonSays = () => {
  const colors = ['#00d4ff', '#ff3366', '#00ff88', '#ffd700'];
  const [sequence, setSequence] = useState([]);
  const [playerSequence, setPlayerSequence] = useState([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [activeColor, setActiveColor] = useState(null);
  const [gameOver, setGameOver] = useState(false);
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(() => {
    return parseInt(localStorage.getItem('sage_simon_high') || '0');
  });

  const playSequence = async (seq) => {
    setIsPlaying(true);
    for (let i = 0; i < seq.length; i++) {
      await new Promise(r => setTimeout(r, 400));
      setActiveColor(seq[i]);
      await new Promise(r => setTimeout(r, 400));
      setActiveColor(null);
    }
    setIsPlaying(false);
  };

  const startGame = () => {
    const newSeq = [Math.floor(Math.random() * 4)];
    setSequence(newSeq);
    setPlayerSequence([]);
    setGameOver(false);
    setScore(0);
    playSequence(newSeq);
  };

  const handleColorClick = (idx) => {
    if (isPlaying || gameOver) return;
    
    const newPlayerSeq = [...playerSequence, idx];
    setPlayerSequence(newPlayerSeq);
    setActiveColor(idx);
    setTimeout(() => setActiveColor(null), 200);
    
    // Check if correct
    if (newPlayerSeq[newPlayerSeq.length - 1] !== sequence[newPlayerSeq.length - 1]) {
      setGameOver(true);
      if (score > highScore) {
        setHighScore(score);
        localStorage.setItem('sage_simon_high', score.toString());
      }
      return;
    }
    
    // Check if completed sequence
    if (newPlayerSeq.length === sequence.length) {
      const newScore = score + 1;
      setScore(newScore);
      setPlayerSequence([]);
      const newSeq = [...sequence, Math.floor(Math.random() * 4)];
      setSequence(newSeq);
      setTimeout(() => playSequence(newSeq), 1000);
    }
  };

  return (
    <div className="flex flex-col items-center" data-testid="simon-game">
      <div className="flex justify-between w-full max-w-xs mb-4">
        <div>
          <p className="font-mono text-[9px] text-[#4a4a60] uppercase">Score</p>
          <p className="font-mono text-xl text-[#00d4ff]">{score}</p>
        </div>
        <div>
          <p className="font-mono text-[9px] text-[#4a4a60] uppercase">Best</p>
          <p className="font-mono text-xl text-[#ffd700]">{highScore}</p>
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-3 mb-6">
        {colors.map((color, idx) => (
          <button
            key={idx}
            onClick={() => handleColorClick(idx)}
            disabled={isPlaying}
            className={`w-28 h-28 rounded-xl transition-all duration-200 ${
              activeColor === idx 
                ? 'scale-95 brightness-150' 
                : 'hover:scale-105'
            }`}
            style={{ 
              backgroundColor: `${color}${activeColor === idx ? 'ff' : '40'}`,
              border: `2px solid ${color}`,
              boxShadow: activeColor === idx ? `0 0 30px ${color}` : 'none'
            }}
          />
        ))}
      </div>
      
      {gameOver && (
        <div className="text-center mb-4 px-6 py-3 rounded-lg border border-[#ff3366]/50 bg-[#ff3366]/10">
          <p className="text-[#ff3366] font-mono text-sm uppercase">Game Over!</p>
        </div>
      )}
      
      <button
        onClick={startGame}
        className="flex items-center gap-2 px-4 py-2 border border-[#00d4ff]/30 text-[#00d4ff] font-mono text-xs uppercase tracking-wider hover:bg-[#00d4ff]/10 transition-colors rounded"
      >
        {sequence.length === 0 ? <Sparkles className="w-3.5 h-3.5" /> : <RotateCcw className="w-3.5 h-3.5" />}
        {sequence.length === 0 ? 'Start' : 'Restart'}
      </button>
    </div>
  );
};

// ============================================
// MAIN GAMES COMPONENT
// ============================================
export default function Games() {
  const [selectedGame, setSelectedGame] = useState(null);
  const [showLeaderboard, setShowLeaderboard] = useState(false);

  const games = [
    { id: 'tictactoe', name: 'Tic-Tac-Toe', icon: Grid3X3, desc: 'Classic strategy game vs AI', color: '#00d4ff' },
    { id: 'memory', name: 'Memory Match', icon: Brain, desc: 'Test your memory', color: '#ff3366' },
    { id: 'reaction', name: 'Reaction Test', icon: Zap, desc: 'How fast are you?', color: '#00ff88' },
    { id: 'scramble', name: 'Word Scramble', icon: Type, desc: 'Unscramble tech words', color: '#ffd700' },
    { id: 'trivia', name: 'Tech Trivia', icon: HelpCircle, desc: 'Test your tech knowledge', color: '#00d4ff' },
    { id: 'simon', name: 'Simon Says', icon: Cpu, desc: 'Pattern memory game', color: '#ff3366' }
  ];

  // Get leaderboard data from localStorage
  const getLeaderboard = () => {
    return [
      { game: 'Reaction Test', score: localStorage.getItem('sage_reaction_best') || '---', unit: 'ms', lower: true },
      { game: 'Simon Says', score: localStorage.getItem('sage_simon_high') || '0', unit: 'rounds', lower: false },
      { game: 'Trivia Best', score: localStorage.getItem('sage_trivia_best') || '0', unit: 'pts', lower: false },
    ];
  };

  const renderGame = () => {
    switch (selectedGame) {
      case 'tictactoe': return <TicTacToe />;
      case 'memory': return <MemoryMatch />;
      case 'reaction': return <ReactionTime />;
      case 'scramble': return <WordScramble />;
      case 'trivia': return <Trivia />;
      case 'simon': return <SimonSays />;
      default: return null;
    }
  };

  // Leaderboard Modal
  if (showLeaderboard) {
    return (
      <div className="min-h-screen bg-[#08080c] pb-20" data-testid="leaderboard-screen">
        <div className="p-4 border-b border-[#1a1a24]">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 border border-[#ffd700]/30 rounded-lg flex items-center justify-center bg-[#ffd700]/5">
                <Trophy className="w-5 h-5 text-[#ffd700]" style={{ filter: 'drop-shadow(0 0 4px rgba(255, 215, 0, 0.6))' }} />
              </div>
              <div>
                <h1 className="font-mono text-xs text-[#ffd700] uppercase tracking-wider">Leaderboard</h1>
                <p className="font-mono text-[9px] text-[#4a4a60]">Your best scores</p>
              </div>
            </div>
            <button
              onClick={() => setShowLeaderboard(false)}
              className="font-mono text-xs text-[#4a4a60] hover:text-[#00d4ff] transition-colors"
            >
              ← Back
            </button>
          </div>
        </div>

        <div className="p-4 space-y-3">
          {getLeaderboard().map((entry, idx) => (
            <div 
              key={entry.game}
              className="bg-[#0d0d12] border border-[#1a1a24] rounded-lg p-4 flex items-center justify-between"
            >
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-mono font-bold ${
                  idx === 0 ? 'bg-[#ffd700]/20 text-[#ffd700]' :
                  idx === 1 ? 'bg-[#c0c0c0]/20 text-[#c0c0c0]' :
                  'bg-[#cd7f32]/20 text-[#cd7f32]'
                }`}>
                  {idx + 1}
                </div>
                <div>
                  <p className="font-mono text-xs text-[#00d4ff]">{entry.game}</p>
                  <p className="font-mono text-[9px] text-[#4a4a60]">{entry.lower ? 'Lower is better' : 'Higher is better'}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="font-mono text-lg text-[#e8e8f0]">{entry.score}</p>
                <p className="font-mono text-[9px] text-[#4a4a60]">{entry.unit}</p>
              </div>
            </div>
          ))}
          
          <div className="mt-6 p-4 bg-[#0d0d12] border border-[#1a1a24] rounded-lg">
            <p className="font-mono text-[10px] text-[#4a4a60] uppercase tracking-wider mb-2">Coming Soon</p>
            <p className="font-mono text-xs text-[#7a7a9a]">Global leaderboards with friends</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#08080c] pb-20" data-testid="games-screen">
      {/* Header */}
      <div className="p-4 border-b border-[#1a1a24]">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 border border-[#00d4ff]/30 rounded-lg flex items-center justify-center bg-[#00d4ff]/5">
              <Gamepad2 className="w-5 h-5 text-[#00d4ff]" style={{ filter: 'drop-shadow(0 0 4px rgba(0, 212, 255, 0.6))' }} />
            </div>
            <div>
              <h1 className="font-mono text-xs text-[#00d4ff] uppercase tracking-wider">
                {selectedGame ? games.find(g => g.id === selectedGame)?.name : 'Games'}
              </h1>
              <p className="font-mono text-[9px] text-[#4a4a60]">
                {selectedGame ? games.find(g => g.id === selectedGame)?.desc : 'Select a game to play'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!selectedGame && (
              <button
                onClick={() => setShowLeaderboard(true)}
                className="flex items-center gap-1.5 px-3 py-2 border border-[#ffd700]/30 rounded-lg text-[#ffd700] hover:bg-[#ffd700]/10 transition-colors"
                data-testid="leaderboard-btn"
              >
                <Trophy className="w-4 h-4" />
                <span className="font-mono text-[10px] uppercase">Ranks</span>
              </button>
            )}
            {selectedGame && (
              <button
                onClick={() => setSelectedGame(null)}
                className="font-mono text-xs text-[#4a4a60] hover:text-[#00d4ff] transition-colors"
              >
                ← Back
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Game Selection Grid */}
      {!selectedGame && (
        <div className="p-4 grid grid-cols-2 gap-3">
          {games.map((game) => (
            <button
              key={game.id}
              onClick={() => setSelectedGame(game.id)}
              className="bg-[#0d0d12] border border-[#1a1a24] rounded-xl p-4 text-left hover:border-[#00d4ff]/30 transition-all group"
              data-testid={`game-card-${game.id}`}
            >
              <div 
                className="w-12 h-12 rounded-lg flex items-center justify-center mb-3 transition-all group-hover:scale-110"
                style={{ 
                  backgroundColor: `${game.color}10`,
                  border: `1px solid ${game.color}30`
                }}
              >
                <game.icon 
                  className="w-6 h-6" 
                  style={{ 
                    color: game.color,
                    filter: `drop-shadow(0 0 4px ${game.color}60)`
                  }} 
                />
              </div>
              <p className="font-mono text-xs text-[#00d4ff] mb-1">{game.name}</p>
              <p className="font-mono text-[9px] text-[#4a4a60]">{game.desc}</p>
              <div className="flex items-center gap-1 mt-3 text-[#4a4a60] group-hover:text-[#00d4ff] transition-colors">
                <span className="font-mono text-[9px] uppercase">Play</span>
                <ChevronRight className="w-3 h-3" />
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Game Content */}
      {selectedGame && (
        <div className="p-6">
          {renderGame()}
        </div>
      )}
    </div>
  );
}
